import { FormEvent, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { requestAccountDeletion } from '../../services/account'
import { TrashIcon, XIcon } from '../icons'
import '../pos/ConfirmDialog.css'
import './DeleteAccount.css'

/**
 * Hesabı sil (23 Eki 2026, App Store kuralı 5.1.1(v)). Yalnız patronda,
 * Ayarlar > Hesabım'ın en altında.
 *
 * Silme 30 gün sonra: talep gelince kurum kapanıyor, kimse giremiyor; bu
 * sürede patron info@'ya yazarsa sistem yöneticisi geri açıyor. Pencere
 * şifreyi yeniden soruyor: açık bırakılmış bir kasada başkası silemesin.
 */
export function DeleteAccount() {
  const { tenantId, tenantName, logout } = useAuth()
  const [open, setOpen] = useState(false)

  return (
    <section className="acct__box delacct" aria-label="Hesabı sil">
      <h4>Hesabı sil</h4>
      <div className="delacct__row">
        <p className="delacct__text">
          İşletmeniz, menünüz, satış kayıtlarınız ve bütün personel hesapları silinir. Talepten sonra 30 gün içinde
          vazgeçebilirsiniz.
        </p>
        <button type="button" className="btn btn--outline delacct__open" onClick={() => setOpen(true)}>
          <TrashIcon />
          Hesabımı sil
        </button>
      </div>

      {open && tenantId && (
        <DeleteDialog
          tenantId={tenantId}
          tenantName={tenantName ?? tenantId}
          onClose={() => setOpen(false)}
          onDone={() => void logout()}
        />
      )}
    </section>
  )
}

function DeleteDialog({
  tenantId,
  tenantName,
  onClose,
  onDone,
}: {
  tenantId: string
  tenantName: string
  onClose: () => void
  onDone: () => void
}) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const until = new Date(Date.now() + 30 * 86_400_000).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!password || busy) return
    setBusy(true)
    setError(null)
    try {
      await requestAccountDeletion(tenantId, password)
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'İşlem tamamlanamadı.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="dlg" role="dialog" aria-modal="true" aria-label="Silme talebi alındı">
        <div className="dlg__card dlg__card--ask delacct__card">
          <h2>Silme talebiniz alındı</h2>
          <div className="delacct__info">
            <span>{tenantName} kapatıldı. Bu cihazdan ve diğer cihazlardan çıkış yapılıyor.</span>
            <span>
              Bütün verileriniz <b>{until}</b> tarihinden sonra kalıcı olarak silinecek. Vazgeçerseniz o tarihe kadar{' '}
              <b>info@adissoo.com</b> adresine yazın.
            </span>
          </div>
          <div className="dlg__buttons">
            <button type="button" className="btn btn--primary" onClick={onDone}>
              Tamam
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-label="Hesabı sil">
      <form className="dlg__card dlg__card--ask dlg__card--x delacct__card" onSubmit={submit}>
        <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat">
          <XIcon />
        </button>
        <h2>Hesabınız silinsin mi?</h2>

        <ul className="delacct__list">
          <li>
            <b>{tenantName}</b> hemen kapanır. Siz ve personeliniz giriş yapamazsınız, QR menünüz kapanır.
          </li>
          <li>Menü, masalar, satışlar, raporlar, cari hesaplar ve bütün kullanıcılar {until} sonrası kalıcı olarak silinir.</li>
          <li>Bu tarihe kadar vazgeçebilirsiniz. Sonrasında veriler geri getirilemez.</li>
        </ul>

        <label className="dlgfield">
          <span>Onaylamak için şifreniz</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="Giriş şifreniz"
          />
        </label>
        {error && (
          <p className="delacct__err" role="alert">
            {error}
          </p>
        )}

        <div className="dlg__buttons dlg__buttons--even">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Vazgeç
          </button>
          <button type="submit" className="btn btn--danger" disabled={!password || busy}>
            {busy ? 'Siliniyor…' : 'Hesabımı sil'}
          </button>
        </div>
      </form>
    </div>
  )
}
